// frontend/src/components/admin/PasswordResetManagement.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './PasswordResetManagement.css';

const PasswordResetManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsersWithResetTokens();
  }, []);

  const fetchUsersWithResetTokens = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await api.get(
        `/admin/users`,
        {
          params: { limit: 100 },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Filter users who have active reset tokens
      const usersWithTokens = response.data.users.filter(u =>
        u.user.password_reset_token && u.user.password_reset_expires
      );

      setUsers(usersWithTokens);
      setError('');
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load password reset data.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualReset = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetModal(true);
  };

  const executeManualReset = async () => {
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');

      // Update user password directly
      await api.put(
        `/admin/users/${selectedUser.user.id}`,
        {
          password: newPassword  // You may need to add this endpoint
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowResetModal(false);
      alert('Password reset successfully');
      fetchUsersWithResetTokens();
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password. You may need to implement the password reset endpoint.');
    }
  };

  const sendResetEmail = async (email) => {
    try {
      const token = localStorage.getItem('auth_token');
      await api.post(
        `/auth/forgot-password`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Password reset email sent to ${email}`);
    } catch (err) {
      console.error('Failed to send reset email:', err);
      alert('Failed to send reset email');
    }
  };

  const invalidateResetToken = async (user) => {
    if (!window.confirm(`Invalidate reset token for ${user.user.email}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');

      // Update user to clear reset token
      await api.put(
        `/admin/users/${user.user.id}`,
        {
          email: user.user.email  // Just update to trigger clearing token
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Reset token invalidated');
      fetchUsersWithResetTokens();
    } catch (err) {
      console.error('Failed to invalidate token:', err);
      alert('Failed to invalidate token');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isTokenExpired = (expiresDate) => {
    return new Date(expiresDate) < new Date();
  };

  if (loading) {
    return <div className="loading">Loading password reset data...</div>;
  }

  return (
    <div className="password-reset-management">
      <div className="password-reset-header">
        <h2>🔐 Password Reset Management</h2>
        <div className="header-stats">
          <div className="stat-box">
            <span className="stat-label">Active Reset Tokens</span>
            <span className="stat-value">
              {users.filter(u => !isTokenExpired(u.user.password_reset_expires)).length}
            </span>
          </div>
          <div className="stat-box expired">
            <span className="stat-label">Expired Tokens</span>
            <span className="stat-value">
              {users.filter(u => isTokenExpired(u.user.password_reset_expires)).length}
            </span>
          </div>
        </div>
      </div>

      {/* Manual Reset Section */}
      <div className="manual-reset-section">
        <h3>📧 Send Reset Email</h3>
        <div className="search-reset">
          <input
            type="email"
            placeholder="Enter user email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="email-search-input"
          />
          <button
            className="btn-send-reset"
            onClick={() => sendResetEmail(searchEmail)}
            disabled={!searchEmail}
          >
            Send Reset Email
          </button>
        </div>
        <p className="help-text">
          Enter a user's email to send them a password reset link
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Active Reset Tokens Table */}
      {users.length > 0 ? (
        <div className="tokens-section">
          <h3>Active & Expired Reset Tokens</h3>
          <div className="tokens-table-container">
            <table className="tokens-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Token Status</th>
                  <th>Expires</th>
                  <th>Token (First 16 chars)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((userRecord) => {
                  const user = userRecord.user;
                  const expired = isTokenExpired(user.password_reset_expires);

                  return (
                    <tr key={user.id} className={expired ? 'expired-row' : ''}>
                      <td className="email-cell">{user.email}</td>
                      <td>
                        {expired ? (
                          <span className="badge badge-expired">Expired</span>
                        ) : (
                          <span className="badge badge-active">Active</span>
                        )}
                      </td>
                      <td>
                        <span className={expired ? 'expired-date' : ''}>
                          {formatDate(user.password_reset_expires)}
                        </span>
                      </td>
                      <td>
                        <code className="token-preview">
                          {user.password_reset_token.substring(0, 16)}...
                        </code>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-reset"
                            onClick={() => handleManualReset(userRecord)}
                            title="Manually reset password"
                          >
                            🔑 Reset
                          </button>
                          <button
                            className="btn-resend"
                            onClick={() => sendResetEmail(user.email)}
                            title="Resend reset email"
                          >
                            📧 Resend
                          </button>
                          <button
                            className="btn-invalidate"
                            onClick={() => invalidateResetToken(userRecord)}
                            title="Invalidate token"
                          >
                            ❌ Invalidate
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="no-tokens">
          <p>✅ No active or expired reset tokens found</p>
        </div>
      )}

      {/* Manual Reset Modal */}
      {showResetModal && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>🔑 Manually Reset Password</h3>
            <p className="modal-subtitle">
              Reset password for: <strong>{selectedUser.user.email}</strong>
            </p>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                autoFocus
              />
              <small className="help-text">Password must be at least 8 characters long</small>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowResetModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={executeManualReset}>
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordResetManagement;